import { at } from 'lodash-es';

import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import { asSegments } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import {
  type AssetsEditSegmentSchemaPartial,
  type AssetsHttpGetAssetsByAssetIdSegmentsQuery,
  type AssetsHttpPostAssetsByAssetIdSegmentsHeader,
  type AssetsSegmentElasticSchemaWithIdNullable,
  type AssetsSegmentSchema,
  type AssetsSegmentSchemaWithIdNullable,
  httpGetAssetsByAssetIdSegments,
  httpGetAssetsByAssetIdSegmentsBySegmentId,
  httpPatchAssetsByAssetIdSegmentsBySegmentId,
  httpPostAssetsByAssetIdSegments,
} from '@iconik-io/iconik-api/src/services/assets';
import { httpGetUsersBasic } from '@iconik-io/iconik-api/src/services/users';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';
import { normalizeEntities } from '@iconik-io/shared-lib/src/redux/entities/utils';

import type { AppDispatch, GetState } from '../../store/types';
import { assetCommentsReceived, setAssetCommentsLoading } from './slice';

export const editComment =
  (assetId: string, commentId: string, data: AssetsEditSegmentSchemaPartial) =>
  (dispatch: AppDispatch) =>
    dispatch(
      httpPatchAssetsByAssetIdSegmentsBySegmentId(assetId, commentId, data, {
        index_immediately: 'true',
      }),
    );

export const deleteComment =
  (assetId: string, commentId: string) => (dispatch: AppDispatch) =>
    dispatch(
      httpPatchAssetsByAssetIdSegmentsBySegmentId(
        assetId,
        commentId,
        {
          status: 'DELETED',
        },
        {
          index_immediately: 'true',
        },
      ),
    );

export const createComment =
  (
    assetId: string,
    comment: AssetsSegmentSchema,
    shareLinkUserEmail: string | undefined,
  ) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    const headers: AssetsHttpPostAssetsByAssetIdSegmentsHeader = {};

    if (shareLinkUserEmail) {
      headers['Share-User-Email'] = shareLinkUserEmail;
    }
    const { body } = await dispatch(
      httpPostAssetsByAssetIdSegments(assetId, comment, headers),
    );
    let updateData = body;
    if (body.user_id) {
      let basicUser = getState().entities.users_basic[body.user_id];
      if (!basicUser) {
        await dispatch(httpGetUsersBasic({ ids: body.user_id }));
      }
      basicUser = getState().entities.users_basic[body.user_id];
      if (basicUser) {
        const { first_name, last_name, photo } = basicUser;
        updateData = {
          ...body,
          user_first_name: first_name ?? undefined,
          user_last_name: last_name ?? undefined,
          user_photo: photo,
        };
      }
    }
    dispatch(updateComments(assetId, [updateData]));

    return body;
  };

const defaultParams = {
  segment_type: 'COMMENT',
  sort: 'date_created,asc',
  per_page: 100,
  include_all_versions: true,
  page: 1,
};

export const getCommentsSegments =
  (assetId: string, params?: AssetsHttpGetAssetsByAssetIdSegmentsQuery) =>
  (dispatch: AppDispatch) => {
    const requestParams: AssetsHttpGetAssetsByAssetIdSegmentsQuery = {
      ...defaultParams,
      ...params,
      include_users: true,
    };

    let comments: AssetsSegmentElasticSchemaWithIdNullable[] = [];

    const resolve = (
      {
        body,
      }: EndpointResponse<ReturnType<typeof httpGetAssetsByAssetIdSegments>>,
      page: number | undefined,
    ): Promise<AssetsSegmentElasticSchemaWithIdNullable[]> => {
      comments = comments.concat(body.objects);

      if (page && body.pages > page) {
        return dispatch(
          httpGetAssetsByAssetIdSegments(assetId, {
            ...requestParams,
            page: page + 1,
          }),
        ).then((response) => resolve(response, page + 1));
      }

      return Promise.resolve(comments);
    };

    dispatch(setAssetCommentsLoading({ assetId, loading: true }));

    // Recursively get all pages from the comments
    return dispatch(httpGetAssetsByAssetIdSegments(assetId, requestParams))
      .then((response) => resolve(response, requestParams.page))
      .then((allComments) => {
        dispatch(updateComments(assetId, allComments));
        dispatch(setAssetCommentsLoading({ assetId, loading: false }));
      });
  };

export const updateComments =
  (
    assetId: string,
    comments: AssetsSegmentSchemaWithIdNullable[],
    withEntities = false,
  ) =>
  (dispatch: AppDispatch) => {
    const commentIds: string[] = [];
    const relations: Record<string, string[]> = {};
    comments.forEach(({ id, parent_id }) => {
      if (parent_id) {
        relations[parent_id] = relations[parent_id]
          ? relations[parent_id].concat(id)
          : [id];
      } else {
        commentIds.push(id);
      }
    });

    if (withEntities) {
      const { entities } = normalizeEntities(asSegments(comments), 'segments');
      dispatch(updateEntities(entities));
    }
    dispatch(
      assetCommentsReceived({ assetId, segmentIds: commentIds, relations }),
    );
  };

export const fetchCommentDrawing =
  (assetId: string, segmentId: string) => (dispatch: AppDispatch) => {
    dispatch(httpGetAssetsByAssetIdSegmentsBySegmentId(assetId, segmentId));
  };

export const fetchCommentDrawings =
  (segmentIds: readonly string[]) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const segments = at(getState().entities.segments, segmentIds).filter(
      (segment) => segment,
    );
    return Promise.all(
      segments.map(({ asset_id: assetId, id }) =>
        dispatch(fetchCommentDrawing(assetId, id)),
      ),
    );
  };
